import React, { useMemo } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { AccommodationsScenario } from '@shared/types/Accommodations.types';
import type { FeaturedPlacementVariant } from './hooks/useFeaturedPlacements.types';

/**
 * The attribution fields every hotel IMPRESSION and CLICK event in the console carries, so the
 * funnel can be read per experiment arm, per surface and per position without joining to anything.
 *
 * A context rather than props because the click handlers sit in five components at four depths
 * (`HotelCTA`, `ListingEntry`, `ListingEntryMobileV2`, `useListingEntryBase`, `AdminHotelCTA`), all
 * reached through the shared `renderListingEntry` / `renderListingCta` render props — threading a
 * pair of telemetry-only values through those signatures would touch every scenario tile for no
 * behavioural reason.
 *
 * The values are RESOLVED ONCE, in `AccommodationsConsole`, and only read here. No consumer may call
 * `useFeatureValue('staysFeaturedPlacementExperiment')` itself: each read is an Amplitude exposure,
 * and a leaf that re-read the flag would record one exposure per rendered tile.
 */
export type FeaturedPlacementTelemetryFields = Readonly<{
  /**
   * The arm this searcher is bucketed into, or undefined when this scenario is not in the experiment
   * at all (`cashFund`, `honeymoon`, `ota`).
   *
   * Undefined and `'control'` are DIFFERENT and must not be conflated: `control` is the in-experiment
   * baseline arm, undefined is "never bucketed". Both are on the event so the data describes itself
   * — the arm does not have to be recovered from Amplitude's experiment join, which raw exports and
   * warehouse copies do not carry.
   */
  featuredPlacementVariant?: FeaturedPlacementVariant;
  /**
   * Which console surface the event happened on, so impressions and clicks can be read per surface
   * rather than pooled across five consoles that behave differently.
   */
  scenario?: AccommodationsScenario;
  /**
   * 1-based position of this listing INSIDE the featured unit — `1` for the hero, and the card's
   * place in the band for the row. Undefined for an ordinary tile, which has no featured position.
   *
   * This is the only way to tell whether the row actually works. Its cards past the first screenful
   * sit behind the carousel, so a guest may never scroll to them: without position, a band whose
   * every impression and click belongs to card 1 is indistinguishable from one where all five earn
   * their place, and "the row beat the hero" could just mean "card 1 beat the hero".
   */
  placementPosition?: number;
}>;

const EMPTY_FIELDS: FeaturedPlacementTelemetryFields = {};

/**
 * The shared helper rather than React's `createContext` directly: it names the context and routes a
 * missing-provider read into New Relic (NREG-3036). The ESLint rule that would enforce this
 * (`joy/no-react-create-context`) is off pending a large migration, so review is the only gate.
 *
 * `strict: false` on purpose. Telemetry attribution must never be able to crash a hotel search: a
 * tile rendered outside the provider reports no arm, which is the truth, rather than throwing.
 */
const [FeaturedPlacementTelemetryProvider, useTelemetryContext] = createContext<FeaturedPlacementTelemetryFields>({
  name: 'FeaturedPlacementTelemetry',
  initialValue: EMPTY_FIELDS,
  strict: false,
});

export { FeaturedPlacementTelemetryProvider };

/** All fields undefined outside the provider — honest: nothing there knows the arm or the surface. */
export const useFeaturedPlacementTelemetry = (): FeaturedPlacementTelemetryFields => useTelemetryContext() ?? EMPTY_FIELDS;

/**
 * Stamps a position onto the arm and surface already in scope, for one listing inside a featured
 * unit.
 *
 * Nested rather than a second provider so the arm and surface cannot drift from the position: the
 * band only knows where a card sits, and the console only knows the arm. Anything that reports a
 * position is by definition inside a featured unit, so the two always travel together.
 */
export const FeaturedPlacementPosition = ({ position, children }: Readonly<{ position: number; children: React.ReactNode }>) => {
  const inherited = useFeaturedPlacementTelemetry();
  const value = useMemo<FeaturedPlacementTelemetryFields>(() => ({ ...inherited, placementPosition: position }), [inherited, position]);

  return <FeaturedPlacementTelemetryProvider value={value}>{children}</FeaturedPlacementTelemetryProvider>;
};
